import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
} from '@mui/icons-material';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Configure PDF.js worker - use same CDN as working StoragePDFViewer
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// eslint-disable-next-line no-console
console.log('PDF.js version:', pdfjs.version);
// eslint-disable-next-line no-console
console.log('Worker URL:', pdfjs.GlobalWorkerOptions.workerSrc);

// Test if worker loads
fetch(pdfjs.GlobalWorkerOptions.workerSrc)
  .then((response) => {
    if (response.ok) {
      // eslint-disable-next-line no-console
      console.log('PDF.js worker loaded successfully');
    } else {
      // eslint-disable-next-line no-console
      console.error('PDF.js worker failed to load:', response.status, response.statusText);
    }
  })
  .catch((error) => {
    // eslint-disable-next-line no-console
    console.error('PDF.js worker failed to load:', error);
  });

const getPdfSourceType = (pdfFile) => {
  if (!pdfFile) return 'none';
  if (typeof pdfFile === 'string') return 'url';
  if (pdfFile instanceof Blob) return 'blob';
  return 'unknown';
};

export default function ScrollablePdfViewer({ pdfFile, onPageClick, highlightedPages = [] }) {
  const [numPages, setNumPages] = useState(null);
  const [loading, setLoading] = useState(() => Boolean(pdfFile));
  const [error, setError] = useState(() => null);
  const [scale, setScale] = useState(1.0);
  const [pdfUrl, setPdfUrl] = useState(() => null);
  const loadingTimeoutRef = useRef(null);

  const pdfSourceType = useMemo(() => getPdfSourceType(pdfFile), [pdfFile]);

  const validationError = useMemo(() => {
    if (!pdfFile) {
      return null;
    }
    if (pdfSourceType === 'blob') {
      if (pdfFile.type && pdfFile.type !== 'application/pdf') {
        return 'Fichier PDF invalide';
      }
      return null;
    }
    if (pdfSourceType === 'url') {
      if (pdfFile.trim().length === 0) {
        return 'URL PDF invalide';
      }
      return null;
    }
    if (pdfSourceType === 'unknown') {
      return 'Fichier PDF invalide';
    }
    return null;
  }, [pdfFile, pdfSourceType]);

  useEffect(() => {
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }

    if (!pdfFile) {
      setPdfUrl(null);
      setNumPages(null);
      setLoading(false);
      setError(null);
      return undefined;
    }

    if (validationError) {
      setPdfUrl(null);
      setNumPages(null);
      setLoading(false);
      setError(validationError);
      return undefined;
    }

    if (pdfSourceType === 'unknown') {
      setPdfUrl(null);
      setNumPages(null);
      setLoading(false);
      setError('Source de PDF invalide');
      return undefined;
    }

    setLoading(true);
    setError(null);
    setNumPages(null);

    let url;
    let shouldRevokeUrl = false;

    if (pdfSourceType === 'blob') {
      try {
        url = URL.createObjectURL(pdfFile);
        shouldRevokeUrl = true;
        setPdfUrl(url);
      } catch (urlError) {
        // eslint-disable-next-line no-console
        console.error('Error creating object URL:', urlError);
        setError('Erreur lors de la création de l\'URL du PDF');
        setLoading(false);
        return undefined;
      }
    } else if (pdfSourceType === 'url') {
      url = pdfFile;
      setPdfUrl(url);
    }

    loadingTimeoutRef.current = setTimeout(() => {
      // eslint-disable-next-line no-console
      console.warn('PDF loading timeout - still loading after 15 seconds');
      setError('Le PDF prend trop de temps à charger. Veuillez réessayer.');
      setLoading(false);
    }, 15000);

    return () => {
      if (shouldRevokeUrl && url) {
        URL.revokeObjectURL(url);
      }
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    };
  }, [pdfFile, pdfSourceType, validationError]);

  const totalPages = useMemo(() => numPages ?? 0, [numPages]);

  const pages = useMemo(
    () => Array.from({ length: totalPages }, (_, index) => index + 1),
    [totalPages],
  );

  const highlightedSet = useMemo(
    () => new Set(highlightedPages),
    [highlightedPages],
  );

  const handleDocumentLoadSuccess = useCallback(({ numPages: pagesCount }) => {
    // eslint-disable-next-line no-console
    console.log('PDF loaded successfully, pages:', pagesCount);

    // Clear the loading timeout
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }

    setNumPages(pagesCount);
    setLoading(false);
  }, []);

  const handleDocumentLoadError = useCallback((err) => {
    // eslint-disable-next-line no-console
    console.error('Erreur lors du chargement du PDF:', err);

    // Clear the loading timeout
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }

    // Provide more specific error messages
    let errorMessage = 'Erreur lors du chargement du PDF';
    if (err.message) {
      if (err.message.includes('Invalid PDF')) {
        errorMessage = 'Le fichier PDF est corrompu ou invalide';
      } else if (err.message.includes('password')) {
        errorMessage = 'Le PDF est protégé par mot de passe';
      } else if (err.message.includes('network')) {
        errorMessage = 'Erreur réseau lors du chargement du PDF';
      }
    }

    setError(errorMessage);
    setLoading(false);
  }, []);

  const handleZoomIn = useCallback(() => {
    setScale((prev) => Math.min(prev + 0.2, 3.0));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale((prev) => Math.max(prev - 0.2, 0.5));
  }, []);

  const handlePageClick = useCallback(
    (pageNumber) => {
      if (onPageClick) {
        onPageClick(pageNumber);
      }
    },
    [onPageClick],
  );

  const scrollToPage = useCallback((pageNumber) => {
    const pageElement = document.getElementById(`pdf-page-${pageNumber}`);
    if (pageElement) {
      pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Expose scrollToPage method to parent
  useEffect(() => {
    window.pdfViewerRef = { scrollToPage };
    return () => {
      if (window.pdfViewerRef?.scrollToPage === scrollToPage) {
        window.pdfViewerRef = null;
      }
    };
  }, [scrollToPage]);

  const scalePercent = useMemo(() => Math.round(scale * 100), [scale]);

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (loading && !pdfUrl) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          p: 3,
        }}
      >
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="body1">Chargement du PDF...</Typography>
      </Box>
    );
  }

  if (!pdfUrl) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          p: 3,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          Aucun PDF sélectionné
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#f5f5f5',
      }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1,
          py: 0.5,
          backgroundColor: 'white',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="body2">
          {totalPages}
          {' '}
          page
          {totalPages > 1 ? 's' : ''}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Tooltip title="Zoom avant">
            <IconButton size="small" onClick={handleZoomOut} disabled={scale <= 0.5}>
              <ZoomOutIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="body2" sx={{ minWidth: 60, textAlign: 'center' }}>
            {scalePercent}
            %
          </Typography>
          <Tooltip title="Zoom arrière">
            <IconButton size="small" onClick={handleZoomIn} disabled={scale >= 3.0}>
              <ZoomInIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* PDF Pages */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
          position: 'relative',
        }}
      >
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
            }}
          >
            <CircularProgress size={48} />
          </Box>
        )}
        <Document
          file={pdfUrl}
          onLoadSuccess={handleDocumentLoadSuccess}
          onLoadError={handleDocumentLoadError}
        >
          {pages.map((pageNumber) => (
            <Paper
              key={`page_${pageNumber}`}
              id={`pdf-page-${pageNumber}`}
              elevation={highlightedSet.has(pageNumber) ? 8 : 2}
              sx={{
                position: 'relative',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                border: highlightedSet.has(pageNumber) ? 2 : 0,
                borderColor: 'primary.main',
                marginBottom: 2,
                '&:hover': {
                  elevation: 4,
                },
              }}
              onClick={() => handlePageClick(pageNumber)}
            >
              <Page
                pageNumber={pageNumber}
                scale={scale}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                }}
              >
                Page
                {' '}
                {pageNumber}
              </Box>
            </Paper>
          ))}
        </Document>
      </Box>
    </Box>
  );
}
